import { useMemo } from "react";
import { Card, CardBody, CardTitle, Col, Row } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import {
  CONTROL_MAPPING_TYPE_COLORS,
  CONTROL_MAPPING_TYPE_ORDER,
} from "src/modules/data-protection/constants/Common";

const CONTROL_MAPPING_TYPE_CHART_COLORS = CONTROL_MAPPING_TYPE_ORDER.map(
  (type) => CONTROL_MAPPING_TYPE_COLORS[type]
);

const CHART_SIZE = 220;

const MappingQualitySection = ({ t, dashboardData }) => {
  const mappingQualityCategories = useMemo(() => {
    const byType = dashboardData?.mapping_quality?.by_type || {};

    return CONTROL_MAPPING_TYPE_ORDER.map((type) => {
      const count = Number(byType?.[type] || 0);
      return {
        title: type,
        amount: count,
      };
    });
  }, [dashboardData]);

  const chartSeries = useMemo(() => {
    return mappingQualityCategories.map((item) => Number(item.amount || 0));
  }, [mappingQualityCategories]);

  const chartOptions = useMemo(() => {
    return {
      labels: mappingQualityCategories.map((item) =>
        t(item.title.charAt(0).toUpperCase() + item.title.slice(1))
      ),
      colors: CONTROL_MAPPING_TYPE_CHART_COLORS,
      legend: { show: false },
      dataLabels: {
        enabled: true,
        formatter: (value) => `${Math.round(value)}%`,
        style: {
          colors: ["#212529", "#212529", "#212529", "#212529"],
        },
        dropShadow: { enabled: false },
      },
      stroke: {
        width: 2,
        colors: ["#ffffff"],
      },
      plotOptions: {
        pie: {
          donut: {
            size: "60%",
          },
        },
      },
    };
  }, [mappingQualityCategories, t]);

  const legendItems = useMemo(() => {
    return mappingQualityCategories.map((item) => ({
      key: item.title,
      label: item.title.charAt(0).toUpperCase() + item.title.slice(1),
      color: CONTROL_MAPPING_TYPE_COLORS[item.title] || "#ADB5BD",
    }));
  }, [mappingQualityCategories]);

  return (
    <Col lg="4" md="12" className="mb-3 d-flex">
      <Card className="card_box_Shadow h-100 w-100">
        <CardBody>
          <CardTitle>{t("Control Mapping Quality")}</CardTitle>

          <Row className="mt-2 align-items-center">
            <Col xs="5" sm="5" md="5" lg="5">
              <p className="mb-3">
                <span className="text-muted d-block">{t("Total Mappings")}</span>
                <strong>{dashboardData?.mapping_quality?.total_mappings || 0}</strong>
              </p>
              <p className="mb-3">
                <span className="text-muted d-block">{t("Mandatory")}</span>
                <strong>{dashboardData?.mapping_quality?.mandatory_mappings || 0}</strong>
              </p>
              <p className="mb-0">
                <span className="text-muted d-block">{t("Unmapped")}</span>
                <strong>{dashboardData?.mapping_quality?.unmapped_controls || 0}</strong>
              </p>
            </Col>

            <Col xs="7" sm="7" md="7" lg="7">
              <div className="d-flex flex-column align-items-center">
                <div
                  className="mapping-quality-chart"
                  style={{ width: CHART_SIZE, height: CHART_SIZE }}
                >
                  <ReactApexChart
                    options={chartOptions}
                    series={chartSeries}
                    type="donut"
                    height={CHART_SIZE}
                    width={CHART_SIZE}
                  />
                </div>

                <div className="mapping-quality-legend d-flex flex-nowrap justify-content-center align-items-center w-100 mt-2 gap-2">
                  {legendItems.map((item) => (
                    <div
                      key={item.key}
                      className="d-flex align-items-center"
                      style={{ gap: 6 }}
                    >
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: item.color,
                          display: "inline-block",
                        }}
                      />
                      <small className="mb-0" style={{ fontSize: 12, lineHeight: 1 }}>
                        {t(item.label)}
                      </small>
                    </div>
                  ))}
                </div>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>
    </Col>
  );
};

export default MappingQualitySection;
